import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import './WorkoutTracker.css';

const key = (n) => n.trim().toLowerCase();
const step = (w) => (w < 10 ? 1 : w < 30 ? 2.5 : 5);
// Effort is one letter per set (l/m/h, '-' = unset), e.g. "mmh". Older rows have one letter for all sets.
const eff = (effort, sets) => Array.from({ length: sets || 3 }, (_, i) => (effort || '').length === 1 ? effort : (effort || '')[i] || '-');
// Reps per set ("12,12,10"); older rows only have one `reps` for every set.
const repsOf = (e) => {
  const a = e.set_reps ? e.set_reps.split(',').map(Number) : [];
  return Array.from({ length: e.sets || 3 }, (_, i) => a[i] ?? a[a.length - 1] ?? e.reps ?? 12);
};
// "70 · 12m 12m 10h" = weight, then reps + level for each set.
const fmtParts = (e) => {
  const lv = eff(e.effort, e.sets);
  const r = repsOf(e);
  const w = e.weight ?? '–';
  // No levels and same reps everywhere (a target, not history): "3x12".
  if (lv.every((c) => c === '-') && r.every((x) => x === r[0])) return { w, s: `${r.length}x${r[0]}` };
  return { w, s: r.map((x, i) => x + (lv[i] === '-' ? '' : lv[i].toUpperCase())).join(' '), sets: r.map((x, i) => ({ r: x, l: lv[i] })) };
};
// The weight is the number you follow, so it is set big; reps/levels stay small beside it.
const Fmt = ({ e, plain }) => { const p = fmtParts(e); return <>{plain ? <b>{p.w}</b> : <b className="wk-w">{p.w}</b>} <span>{p.sets ? p.sets.map((x, i) => <React.Fragment key={i}>{i ? ' ' : ''}{x.l !== '-' ? <i className={`wk-lv wk-lv-${x.l}`}>{x.r}</i> : x.r}</React.Fragment>) : p.s}</span></>; };
// Progress vs the previous time: heavier weight, else more total reps at the same weight.
const gain = (now, prev) => {
  if (!prev || now.weight == null || prev.weight == null) return null;
  const dw = +(now.weight - prev.weight).toFixed(1);
  if (dw > 0) return `+${dw}`;
  const dr = repsOf(now).reduce((a, b) => a + b, 0) - repsOf(prev).reduce((a, b) => a + b, 0);
  return dw === 0 && dr > 0 ? `+${dr} reps` : null;
};
const day = (v) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/Oslo' }).format(new Date(v));
const warn = /ikke|klarte|cheat|bom|fail|vond|smerte/i;

// Your rule + per-set effort: 12 reps -> up in weight, unless sets before the last were already hard (then hold).
// All sets light -> bigger jump. Under 12 reps -> same weight, one more rep (hold if early sets were hard).
const recommend = (last, hist = [last]) => {
  if (last.weight == null) return { ...last, why: 'Set a weight' };
  const sets = eff(last.effort, last.sets).filter((c) => c !== '-');
  const warned = warn.test(last.notes || '');
  const struggled = sets.slice(0, -1).includes('h') || warned;
  const light = sets.length > 0 && sets.every((c) => c === 'l');
  const next = { ...last, effort: null, notes: null };
  const reached = Math.min(...repsOf(last));
  const all = (r) => ({ set_reps: Array(last.sets).fill(r).join(','), reps: r });
  // Sessions in a row (newest first) at this weight; a plateau shows up as the same result repeating.
  const same = []; for (const h of hist) { if (h.weight !== last.weight) break; same.push(h); }
  const topRun = []; for (const h of same) { if (Math.min(...repsOf(h)) < 12) break; topRun.push(h); }
  if (reached >= 12 && !struggled) {
    return { ...next, ...all(10), weight: last.weight + step(last.weight) * (light ? 2 : 1), why: light ? 'All light – big jump up' : 'Go up in weight' };
  }
  // 12 on every set, but hard, 2+ sessions in a row: a plateau, so take a small jump instead of holding forever.
  if (reached >= 12 && !warned && topRun.length >= 2) {
    return { ...next, ...all(10), weight: last.weight + Math.max(1, step(last.weight) / 2), why: `Stuck ${topRun.length} sessions at ${last.weight} – small jump, expect ~10 reps` };
  }
  // Same weight and the same low reps 3 times: back off 10% and rebuild.
  if (reached < 12 && same.length >= 3 && same.slice(0, 3).every((h) => Math.min(...repsOf(h)) === reached)) {
    return { ...next, ...all(12), weight: Math.round((last.weight * 0.9) / 0.5) * 0.5, why: `No progress in ${same.length} sessions – deload 10% and rebuild` };
  }
  if (struggled) return { ...next, why: reached >= 12 ? 'Hold – 12 but hard; repeat once more, then a small jump' : 'Hold – already hard before the last set' };
  return { ...next, ...all(reached + 1), why: `Same weight, ${reached + 1} reps` };
};

// Fixed pick-lists, so matching never depends on how an exercise name or muscle was typed.
const MUSCLES = ['chest', 'back', 'shoulders', 'biceps', 'triceps', 'legs', 'core'];
const KINDS = ['push', 'pull', 'other'];
const STYLES = ['compound', 'isolation'];
// Same lift on different machines/brands; lets a consistent switch (old row -> new row) replace the old one in your usual list.
// Programs are yours: a name plus days, each day a set of muscles. Only that day's muscles show on Today.
const DEFAULT_PROGRAMS = {
  Upper: [{ name: 'Upper', muscles: ['chest', 'back', 'shoulders', 'biceps', 'triceps'] }],
  PPL: [{ name: 'Push', muscles: ['chest', 'shoulders', 'triceps'] }, { name: 'Pull', muscles: ['back', 'biceps'] }, { name: 'Legs', muscles: ['legs', 'core'] }],
  'Upper/Lower': [{ name: 'Upper', muscles: ['chest', 'back', 'shoulders', 'biceps', 'triceps'] }, { name: 'Lower', muscles: ['legs', 'core'] }],
};
const ALL_PROGRAM = [{ name: 'All', muscles: null }];
const MOVEMENTS = ['Row', 'Pulldown', 'Pull-up', 'Chest press', 'Incline press', 'Shoulder press', 'Fly', 'Curl', 'Triceps', 'Lateral raise', 'Squat', 'Leg press', 'Leg curl', 'Leg extension', 'Deadlift', 'Shrug'];
// Same movement on a different brand's machine is a different weight, so brand is a pick too.
const MACHINES = ['Technogym', 'Life Fitness', 'Hammer Strength', 'Machine', 'Cable', 'Barbell', 'Dumbbell', 'Bodyweight'];
const Chips = ({ options, value, onPick }) => (
  <div className="wk-row wk-chips">
    {options.map((x) => <button key={x} className={value === x ? 'on' : ''} onClick={() => onPick(value === x ? '' : x)}>{x}</button>)}
  </div>
);
// Keep `anchors` first, then repeatedly pick the exercise that rests what was just worked:
// opposite push/pull to the previous one, and not the same muscle as either of the last two.
const restOrder = (items, anchors, libMap) => {
  const info = (m) => libMap[key(m.name)] || {};
  const out = [...anchors];
  const rem = items.filter((m) => !anchors.includes(m));
  while (rem.length) {
    const recent = out.slice(-2);
    const prev = recent[recent.length - 1];
    let best = 0, bestCost = Infinity;
    rem.forEach((m, i) => {
      const k = info(m).kind;
      // weights: alternate push/pull (8) > compounds before isolation (7) > rest the muscle (6) > your usual order
      const cost = (info(m).style === 'isolation' && rem.some((r) => info(r).style !== 'isolation' && info(r).kind === k) ? 7 : 0)
        + (prev && k && k === info(prev).kind && k !== 'other' ? 8 : 0)
        + (info(m).muscle && recent.some((r) => info(r).muscle === info(m).muscle) ? 6 : 0)
        + i / 100; // otherwise keep your usual order
      if (cost < bestCost) { best = i; bestCost = cost; }
    });
    out.push(rem.splice(best, 1)[0]);
  }
  return out;
};

const Spark = ({ v }) => {
  if (v.length < 2) return null;
  const lo = Math.min(...v), hi = Math.max(...v);
  const pts = v.map((x, i) => `${1 + (i / (v.length - 1)) * 58},${15 - ((x - lo) / (hi - lo || 1)) * 14}`).join(' ');
  return <svg width="60" height="16" aria-hidden="true"><polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>;
};

// One row per exercise, built from the saved sessions (heaviest entry per session), so it updates itself.
const buildStats = (sessions) => {
  const map = {};
  [...sessions].sort((a, b) => new Date(a.performed_at) - new Date(b.performed_at)).forEach((sess) => {
    const best = {};
    sess.exercises.forEach((e) => { const k = key(e.name); if (!best[k] || (e.weight || 0) > (best[k].weight || 0)) best[k] = e; });
    Object.entries(best).forEach(([k, e]) => { (map[k] = map[k] || { name: e.name, h: [] }).h.push({ ...e, date: sess.performed_at }); });
  });
  return Object.entries(map).map(([k, m]) => {
    const w = m.h.map((x) => x.weight).filter((x) => x != null);
    const start = w[0], now = w[w.length - 1];
    return { k, name: m.name, h: m.h, w, start, now, delta: now - start, pct: start ? ((now - start) / start) * 100 : 0, n: m.h.length, last: m.h[m.h.length - 1].date };
  }).sort((a, b) => b.n - a.n);
};

export default function WorkoutTracker({ user, isDarkMode, onToggleTheme, onSignOut }) {
  const [sessions, setSessions] = useState([]);
  const [done, setDone] = useState({}); // today's draft; lives on the server so every device sees it
  const [open, setOpen] = useState(null); // {name, weight, sets, reps, effort}
  const [extra, setExtra] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [swipe, setSwipe] = useState(null); // {k, dx}
  const [view, setView] = useState('today');
  const [statOpen, setStatOpen] = useState(null);
  const [menu, setMenu] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendEmail, setFriendEmail] = useState('');
  const isAdmin = !!user && user.email === 'san.eriksen@gmail.com';
  const [swaps, setSwaps] = useState({}); // today only: planned exercise key -> the variant you do instead
  const [swapping, setSwapping] = useState(null); // name of the exercise being swapped
  const [libOpen, setLibOpen] = useState(null); // library: one exercise expanded at a time
  const [openGroups, setOpenGroups] = useState({}); // stats: muscle groups start collapsed
  const [picking, setPicking] = useState(null); // 'today' | 'library' while choosing from the catalog
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState('');
  const [library, setLibrary] = useState([]); // push/pull, muscle, machine per exercise
  const libMap = useMemo(() => Object.fromEntries(library.map((l) => [key(l.name), l])), [library]);
  const [programs, setPrograms] = useState(DEFAULT_PROGRAMS);
  const [program, setProgram] = useState('All');
  const [welcomed, setWelcomed] = useState(false);
  const [editing, setEditing] = useState(null); // {orig, name, days:[{name, muscles}]} while editing a split
  const [dayPick, setDayPick] = useState('');
  const days = programs[program] && programs[program].length ? programs[program] : ALL_PROGRAM;
  // The day after whichever day the last session matched best (by muscles trained).
  const { suggested, lastDay } = useMemo(() => {
    const last = sessions[0];
    if (!last || days.length < 2) return { suggested: days[0].name, lastDay: null };
    const score = days.map((d) => last.exercises.filter((e) => (d.muscles || []).includes((libMap[key(e.name)] || {}).muscle)).length);
    const best = score.indexOf(Math.max(...score));
    return { suggested: days[(best + 1) % days.length].name, lastDay: score[best] ? days[best].name : null };
  }, [sessions, libMap, days]);
  const dayNames = days.map((d) => d.name);
  const activeDay = dayNames.includes(dayPick) ? dayPick : suggested;
  const dayMuscles = (days.find((d) => d.name === activeDay) || days[0]).muscles;
  // Swipe left hides an exercise under "Other exercises"; swipe right there brings it back.
  const [pref, setPref] = useState({});
  const sw = useRef(null);
  const [note, setNote] = useState('');
  const [order, setOrder] = useState([]);

  // All of the above is one JSON blob in the database. Edits are saved after a short pause and pushed live
  // to other open devices over a server-sent-events stream; changes arriving from elsewhere are applied here.
  // Paint the whole page (notch, home-indicator area, overscroll) in the app's theme, not just the content box.
  useEffect(() => {
    const root = document.querySelector('.workout-standalone');
    const bg = root && [root, root.parentElement].map((el) => el && getComputedStyle(el).backgroundColor).find((c) => c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent');
    const color = bg || (isDarkMode ? '#1f1f1f' : '#ffffff');
    const prev = [document.documentElement.style.background, document.body.style.background];
    document.documentElement.style.background = color; document.body.style.background = color;
    document.documentElement.style.colorScheme = isDarkMode ? 'dark' : 'light';
    let meta = document.querySelector('meta[name="theme-color"]');
    const oldMeta = meta && meta.content;
    if (meta) meta.content = color;
    return () => { [document.documentElement.style.background, document.body.style.background] = prev; if (meta) meta.content = oldMeta; };
  }, [isDarkMode]);
  useEffect(() => {
    // Own tab title and icon on this page (the site-wide ones belong to the event app).
    const previous = document.title;
    document.title = 'Workout Tracker';
    const svg = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' rx='22' fill='%232563eb'/><g fill='white'><rect x='25' y='46' width='50' height='8' rx='2'/><rect x='20' y='32' width='8' height='36' rx='3'/><rect x='72' y='32' width='8' height='36' rx='3'/><rect x='11' y='39' width='7' height='22' rx='2'/><rect x='82' y='39' width='7' height='22' rx='2'/></g></svg>";
    const icon = document.querySelector('link[rel="icon"]');
    const oldIcon = icon && icon.href;
    if (icon) icon.href = svg;
    const touch = document.createElement('link');
    touch.rel = 'apple-touch-icon'; touch.href = '/workout-icon.png';
    document.head.appendChild(touch);
    return () => { document.title = previous; if (icon && oldIcon) icon.href = oldIcon; touch.remove(); };
  }, []);
  const clientId = useRef(Math.random().toString(36).slice(2));
  const synced = useRef(null); // JSON last saved/received, so we neither echo nor loop
  const [hydrated, setHydrated] = useState(false);
  const pack = (d) => JSON.stringify({ done: d.done || {}, order: d.order || [], note: d.note || '', pref: d.pref || {}, program: d.program || 'All', dayPick: d.dayPick || '', programs: d.programs || DEFAULT_PROGRAMS, swaps: d.swaps || {} });
  const apply = (d) => {
    synced.current = pack(d);
    setDone(d.done || {}); setOrder(d.order || []); setNote(d.note || ''); setPref(d.pref || {}); const pr = d.programs || DEFAULT_PROGRAMS; setPrograms(pr); setProgram(pr[d.program] ? d.program : 'All'); setDayPick(d.dayPick || ''); setSwaps(d.swaps || {});
  };
  useEffect(() => {
    const load = () => api.get('/workouts/state').then((r) => {
      const d = r.data.data || {};
      if (!hydrated0.current && !Object.keys(d).length) { // first run: carry over what this browser had stored
        const get = (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } };
        const old = { done: get('workout-draft'), order: get('workout-order'), note: localStorage.getItem('workout-note'), pref: get('workout-pref'), program: localStorage.getItem('workout-program'), dayPick: localStorage.getItem('workout-day') };
        ['workout-draft', 'workout-order', 'workout-note', 'workout-pref', 'workout-program', 'workout-day'].forEach((k) => localStorage.removeItem(k));
        apply(old); synced.current = null; // null: the effect below saves it
      } else apply(d);
      hydrated0.current = true; setHydrated(true);
    }).catch(() => {});
    const hydrated0 = { current: false };
    load();
    const es = new EventSource('/api/workouts/stream');
    es.onopen = () => { if (hydrated0.current) load(); }; // after a dropped connection, catch up
    es.onmessage = (ev) => { try { const m = JSON.parse(ev.data); if (m.client !== clientId.current) apply(m.data); } catch (e) { /* ignore */ } };
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!hydrated) return undefined;
    const data = { done, order, note, pref, program, dayPick, programs, swaps };
    const json = pack(data);
    if (json === synced.current) return undefined;
    const t = setTimeout(() => { synced.current = json; api.put('/workouts/state', { data, client: clientId.current }).catch(() => {}); }, 400);
    return () => clearTimeout(t);
  }, [hydrated, done, order, note, pref, program, dayPick, programs, swaps]);

  useEffect(() => {
    api.get('/workouts/catalog').then((r) => setCatalog(r.data)).catch(() => {});
    api.get('/workouts/library').then((r) => setLibrary(r.data)).catch(() => {});
    api.get('/workouts').then((r) => setSessions(r.data)).catch(() => toast.error('Could not load sessions'));
  }, []);

  // Per exercise: last time (sessions are newest first), plus how often and where in the session you usually do it.
  const { typical, other } = useMemo(() => {
    const inDay = (n) => !dayMuscles || dayMuscles.includes((libMap[key(n)] || {}).muscle);
    // Frequency counts only sessions that trained this day's muscles, so a rotation (PPL) doesn't dilute it.
    const recent = sessions.filter((sess) => sess.exercises.some((e) => inDay(e.name))).slice(0, 8);
    const map = {};
    [...sessions].reverse().forEach((sess) => sess.exercises.forEach((e) => {
      map[key(e.name)] = { ...(map[key(e.name)] || { name: e.name }), last: { ...e, date: sess.performed_at } };
    }));
    extra.forEach((n) => { if (!map[key(n)]) map[key(n)] = { name: n }; });
    recent.forEach((sess) => {
      const seen = new Set();
      sess.exercises.filter((e) => inDay(e.name)).forEach((e, i) => {
        const m = map[key(e.name)];
        if (seen.has(key(e.name))) return;
        seen.add(key(e.name));
        m.n = (m.n || 0) + 1;
        m.pos = (m.pos || 0) + i;
      });
    });
    const list = Object.values(map).map((m) => ({ ...m, avg: m.n ? m.pos / m.n : 99 }));
    // Switch-up: if the same movement was done on a different machine in the last 2+ sessions that had it,
    // that machine replaces the older one(s) in your usual list.
    const current = new Set(), replaced = new Set();
    const byMove = {}; // movement -> [set of exercise keys per session], newest first
    recent.forEach((sess) => {
      const per = {};
      sess.exercises.forEach((e) => { const mv = (libMap[key(e.name)] || {}).movement; if (mv) (per[mv] = per[mv] || new Set()).add(key(e.name)); });
      Object.entries(per).forEach(([mv, set]) => (byMove[mv] = byMove[mv] || []).push(set));
    });
    Object.entries(byMove).forEach(([mv, sets]) => {
      if (sets.length < 2) return;
      const both = [...sets[0]].filter((k) => sets[1].has(k)); // used in each of the last two sessions with this movement
      if (!both.length) return;
      both.forEach((k) => current.add(k));
      Object.entries(libMap).forEach(([k, l]) => { if (l.movement === mv && !sets[0].has(k)) replaced.add(k); });
    });
    const isTypical = (m) => { const k = key(m.name); return pref[k] === 'pin' ? true : pref[k] === 'hide' ? false
      : done[k] || extra.includes(m.name) ? true : !inDay(m.name) ? false
      : replaced.has(k) ? false : current.has(k) ? true
      : !m.n ? extra.includes(m.name) : m.n >= Math.ceil(recent.length * 0.4); };
    const rank = (m) => { const i = order.indexOf(key(m.name)); return i >= 0 ? i : 100 + m.avg; };
    const sorted = list.sort((x, y) => rank(x) - rank(y));
    // No manual order today: lead with the opposite of how the last session started, then rest the muscles.
    let typical = sorted.filter(isTypical);
    const firstEx = sessions[0] && sessions[0].exercises[0];
    const lastStart = firstEx && (libMap[key(firstEx.name)] || {}).kind;
    if (!order.length && (lastStart === 'push' || lastStart === 'pull')) {
      const want = lastStart === 'push' ? 'pull' : 'push';
      const lead = typical.find((m) => (libMap[key(m.name)] || {}).kind === want);
      if (lead) typical = restOrder(typical, [lead], libMap);
    }
    // A swap puts the variant in the planned exercise's slot (history comes from the variant itself).
    const targets = new Set(Object.values(swaps).map(key));
    const seen = new Set();
    typical = typical.map((m) => (swaps[key(m.name)] ? (map[key(swaps[key(m.name)])] || { name: swaps[key(m.name)] }) : m))
      .filter((m) => !seen.has(key(m.name)) && seen.add(key(m.name)));
    return { typical, other: sorted.filter((m) => !isTypical(m) && !targets.has(key(m.name))) };
  }, [sessions, extra, order, pref, libMap, dayMuscles, done, swaps]);

  // Drag by the handle. No React state while dragging: the card and its neighbours are moved with
  // transforms straight on the DOM, once per animation frame, so it stays smooth; the order is committed on release.
  const typicalRef = useRef([]);
  typicalRef.current = typical;
  const startDrag = (e, from) => {
    e.preventDefault();
    window.getSelection()?.removeAllRanges();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    const cards = [...document.querySelectorAll('.wk-card[data-key]')];
    const items = typicalRef.current.map((m) => {
      const el = cards.find((c) => c.dataset.key === key(m.name));
      const r = el ? el.getBoundingClientRect() : { top: 0, height: 0 };
      return { k: key(m.name), el, top: r.top + window.scrollY, h: r.height };
    });
    const f = items.findIndex((r) => r.k === from);
    if (f < 0 || !items[f].el) return;
    const me = items[f];
    // opaque, lifted look for the card you are holding (the page behind may be any theme colour)
    const root = document.querySelector('.workout-standalone');
    const solid = [root, root && root.parentElement, document.body].map((el) => el && getComputedStyle(el).backgroundColor).find((c) => c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') || '#fff';
    me.el.style.cssText += `;z-index:5;background:${solid};box-shadow:0 6px 18px rgba(0,0,0,.35);transition:none;will-change:transform;`;
    items.forEach((it) => { if (it !== me && it.el) it.el.style.cssText += ';transition:transform .12s ease-out;will-change:transform;'; });
    const startY = e.clientY + window.scrollY;
    let y = e.clientY;
    let t = f;
    let raf = 0;
    const place = () => {
      if (y < 90) window.scrollBy(0, -12);
      else if (y > window.innerHeight - 90) window.scrollBy(0, 12);
      const dy = y + window.scrollY - startY;
      me.el.style.transform = `translate3d(0,${dy}px,0)`;
      const centre = me.top + me.h / 2 + dy;
      const idx = items.findIndex((r) => centre < r.top + r.h);
      const nt = idx < 0 ? items.length - 1 : idx;
      if (nt !== t) {
        t = nt;
        items.forEach((it, i) => {
          if (it === me || !it.el) return;
          const ty = f < t && i > f && i <= t ? -me.h : f > t && i >= t && i < f ? me.h : 0;
          it.el.style.transform = ty ? `translate3d(0,${ty}px,0)` : '';
        });
      }
      raf = requestAnimationFrame(place);
    };
    const move = (ev) => { y = ev.clientY; };
    const up = () => {
      cancelAnimationFrame(raf);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      items.forEach((it) => { if (it.el) { it.el.style.transform = ''; it.el.style.transition = 'none'; it.el.style.zIndex = ''; it.el.style.background = ''; it.el.style.boxShadow = ''; it.el.style.willChange = ''; } });
      if (t !== f) {
        const keys = items.map((r) => r.k);
        keys.splice(t, 0, keys.splice(f, 1)[0]);
        setOrder(keys);
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    raf = requestAnimationFrame(place);
  };

  // An exercise only counts as done once every set has a level; Undo flips that flag but keeps all the data.
  const levelsFull = (x) => eff(x.effort, x.sets).every((c) => c !== '-');
  const log = (entry) => { setDone((d) => ({ ...d, [key(entry.name)]: { ...entry, completed: levelsFull(entry) } })); setOpen(entry); };
  const toEntry = (name, v) => {
    const sets = v.sets || 3;
    const e = eff(v.effort, sets).join('');
    const r = repsOf({ ...v, sets });
    const w = parseFloat(String(v.weight ?? '').replace(',', '.'));
    return { name, weight: Number.isNaN(w) ? null : w, sets, reps: Math.min(...r), set_reps: r.join(','), effort: /^-*$/.test(e) ? null : e, notes: (v.notes || '').trim() || null };
  };
  // Every edit is saved to today's draft straight away; the chevron only expands/collapses.
  const update = (patch) => {
    const n = { ...open, ...patch };
    setOpen(n);
    setDone((d) => {
      const e = toEntry(n.name, n);
      const completed = 'effort' in patch || 'sets' in patch ? levelsFull(e) : !!(d[key(n.name)] && d[key(n.name)].completed);
      return { ...d, [key(n.name)]: { ...e, completed } };
    });
  };
  const setReps = (i, d) => { const a = repsOf(open); a[i] = Math.max(0, a[i] + d); update({ set_reps: a.join(',') }); };
  const undo = (k) => setDone((d) => ({ ...d, [k]: { ...d[k], completed: false } }));
  const remove = (k) => { setDone((d) => { const n = { ...d }; delete n[k]; return n; }); setOpen(null); };
  const bump = (field, delta) => update({ [field]: Math.max(0, +((Number(String(open[field]).replace(',', '.')) || 0) + delta).toFixed(1)) });

  // Pick from the standard catalog (already classified); free text is the fallback.
  const pick = (c) => {
    setQ('');
    const typed = c || (window.prompt('Name of new exercise') || '').trim();
    if (!typed) return;
    const body = c ? { name: c.name, kind: c.kind, muscle: c.muscle, style: c.style, machine: c.machine, movement: c.movement } : { name: typed };
    api.post('/workouts/library', body).then((r) => {
      setLibrary((l) => [...l.filter((x) => x.id !== r.data.id), r.data]);
      if (picking === 'today') { setExtra((x) => [...x, r.data.name]); setOpen({ name: r.data.name, weight: 0, sets: 3, reps: 12, set_reps: '12,12,12', effort: '', notes: '' }); }
    }).catch(() => toast.error('Could not add'));
    setPicking(null);
  };
  const save = async () => {
    const list = Object.values(done).map(({ completed, ...e }) => e);
    if (!list.length) return;
    setSaving(true);
    try {
      const r = await api.post('/workouts', { exercises: list, notes: note.trim() || null });
      setSessions((s) => [r.data, ...s]);
      setDone({}); setSwaps({});
      setNote('');
      setOrder([]); // the next session is ordered from history again
      toast.success('Session saved');
    } catch (e) { toast.error('Could not save'); }
    setSaving(false);
  };

  const removeSession = async (id) => {
    if (!window.confirm('Delete this session for good?')) return;
    try {
      await api.delete(`/workouts/${id}`);
      setSessions((x) => x.filter((y) => y.id !== id));
    } catch (e) { toast.error('Could not delete'); }
  };

  if (view === 'friends') {
    const add = () => {
      const email = friendEmail.trim().toLowerCase();
      if (!email) return;
      api.post('/workouts/friends', { email }).then(() => { setFriends((f) => [...f.filter((x) => x.email !== email), { email }]); setFriendEmail(''); })
        .catch(() => toast.error('Enter a valid email'));
    };
    const remove = (email) => { if (window.confirm(`Remove ${email}? Their data stays, but they can no longer sign in.`)) api.delete(`/workouts/friends/${encodeURIComponent(email)}`).then(() => setFriends((f) => f.filter((x) => x.email !== email))); };
    return (
      <div className="wk">
        <header><b>Friends</b><button onClick={() => setView('today')}>Back</button></header>
        <small className="wk-hint">Invite a friend by their Google email. They can sign in at sandereriksen.com/workout and use the workout tracker only, with their own separate data. They cannot see your data or the rest of the site.</small>
        <input className="wk-in" type="email" placeholder="friend@gmail.com" value={friendEmail} onChange={(e) => setFriendEmail(e.target.value)} />
        <button className="wk-add wk-primary" onClick={add}>Invite</button>
        {friends.map((f) => <div className="wk-lib" key={f.email}><div className="wk-row"><span>{f.email}</span><button onClick={() => remove(f.email)}>Remove</button></div></div>)}
      </div>
    );
  }
  if (view === 'log') {
    const long = (v) => new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Oslo' }).format(new Date(v));
    return (
      <div className="wk">
        <header><b>Log · {sessions.length} sessions</b><button onClick={() => setView('today')}>Back</button></header>
        {sessions.map((x) => (
          <details className="wk-log" key={x.id}>
            <summary>{long(x.performed_at)} · {x.exercises.length} exercises</summary>
            {x.exercises.map((e) => (
              <div key={e.id}>{e.name} <Fmt e={e} plain />{e.notes ? <small> · {e.notes}</small> : null}</div>
            ))}
            {x.notes && <small>Note: {x.notes}</small>}
            <button onClick={() => removeSession(x.id)}>Delete session</button>
          </details>
        ))}
      </div>
    );
  }

  const cardStyle = (k) => (swipe && swipe.k === k
    ? { transform: `translateX(${swipe.dx}px)`, transition: 'none', opacity: 1 - Math.min(Math.abs(swipe.dx) / 300, 0.5) }
    : undefined);

  const saveLib = (l, patch) => {
    const changed = Object.entries(patch).some(([f, v]) => (l[f] || '') !== (v || ''));
    if (!changed) return;
    api.put(`/workouts/library/${l.id}`, patch).then((r) => {
      setLibrary((arr) => arr.map((x) => (x.id === l.id ? r.data : x)));
      if ('name' in patch) api.get('/workouts').then((s) => setSessions(s.data)); // history was renamed too
    }).catch(() => toast.error('Could not save'));
  };
  if (swapping) {
    const src = libMap[key(swapping)] || {};
    const sameMove = library.filter((l) => key(l.name) !== key(swapping) && src.movement && l.movement === src.movement);
    const sameMuscle = library.filter((l) => key(l.name) !== key(swapping) && src.muscle && l.muscle === src.muscle && !sameMove.includes(l));
    const needle = q.trim().toLowerCase();
    const have = new Set(library.map((l) => key(l.name)));
    const more = catalog.filter((c) => !have.has(key(c.name)) && (needle ? `${c.name} ${c.machine}`.toLowerCase().includes(needle) : (src.movement ? c.movement === src.movement : c.muscle === src.muscle))).slice(0, 40);
    const doSwap = (name) => { setSwaps((w) => ({ ...w, [Object.keys(w).find((o) => key(w[o]) === key(swapping)) || key(swapping)]: name })); setSwapping(null); setOpen(null); setQ(''); };
    const fromCatalog = (c) => api.post('/workouts/library', { name: c.name, kind: c.kind, muscle: c.muscle, style: c.style, machine: c.machine, movement: c.movement })
      .then((r) => { setLibrary((l) => [...l.filter((x) => x.id !== r.data.id), r.data]); doSwap(r.data.name); }).catch(() => toast.error('Could not add'));
    const row = (l, fn) => <button className="wk-add" key={l.name} onClick={fn}>{l.name}{l.machine ? ` · ${l.machine}` : ''}</button>;
    return (
      <div className="wk">
        <header><b>Swap {swapping}</b><button onClick={() => { setSwapping(null); setQ(''); }}>Back</button></header>
        <small className="wk-hint">Only changes today. The variant takes this exercise's place in the list.</small>
        <input className="wk-in" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
        {sameMove.length > 0 && <small className="wk-hint">Same movement{src.movement ? ` (${src.movement})` : ''}</small>}
        {sameMove.map((l) => row(l, () => doSwap(l.name)))}
        {sameMuscle.length > 0 && <small className="wk-hint">Same muscle you already have</small>}
        {sameMuscle.map((l) => row(l, () => doSwap(l.name)))}
        {more.length > 0 && <small className="wk-hint">{needle ? 'Search results' : 'From the catalog'}</small>}
        {more.map((c) => row(c, () => fromCatalog(c)))}
      </div>
    );
  }
  if (editing) {
    const upd = (patch) => setEditing((e) => ({ ...e, ...patch }));
    const setDay = (i, patch) => upd({ days: editing.days.map((d, x) => (x === i ? { ...d, ...patch } : d)) });
    const saveSplit = () => {
      const name = editing.name.trim();
      const ds = editing.days.map((d) => ({ name: d.name.trim(), muscles: d.muscles })).filter((d) => d.name && d.muscles.length);
      if (!name || name === 'All' || !ds.length) { toast.error('Name the split and give each day a name and muscles'); return; }
      if (name !== editing.orig && programs[name]) { toast.error('A split with that name exists'); return; }
      setPrograms((p) => { const n = { ...p }; if (editing.orig) delete n[editing.orig]; n[name] = ds; return n; });
      setProgram(name); setDayPick(''); setEditing(null);
    };
    const del = () => {
      if (!window.confirm(`Delete the split "${editing.orig}"?`)) return;
      setPrograms((p) => { const n = { ...p }; delete n[editing.orig]; return n; });
      setProgram('All'); setDayPick(''); setEditing(null);
    };
    return (
      <div className="wk">
        <header><b>{editing.orig ? 'Edit split' : 'New split'}</b><button onClick={() => setEditing(null)}>Cancel</button></header>
        <input className="wk-in" placeholder="Split name (e.g. PPL)" value={editing.name} onChange={(e) => upd({ name: e.target.value })} />
        {editing.days.map((d, i) => (
          <div className="wk-lib" key={i}>
            <input className="wk-in" placeholder={`Day ${i + 1} name (e.g. Push)`} value={d.name} onChange={(e) => setDay(i, { name: e.target.value })} />
            <div className="wk-row wk-chips">
              {MUSCLES.map((mu) => <button key={mu} className={d.muscles.includes(mu) ? 'on' : ''} onClick={() => setDay(i, { muscles: d.muscles.includes(mu) ? d.muscles.filter((x) => x !== mu) : [...d.muscles, mu] })}>{mu}</button>)}
            </div>
            {editing.days.length > 1 && <button onClick={() => upd({ days: editing.days.filter((_, x) => x !== i) })}>Remove day</button>}
          </div>
        ))}
        <button className="wk-add" onClick={() => upd({ days: [...editing.days, { name: '', muscles: [] }] })}>+ Add day</button>
        <button className="wk-add wk-primary" onClick={saveSplit}>Save split</button>
        {editing.orig && <button className="wk-add" onClick={del}>Delete split</button>}
        <small className="wk-hint">Days run in the order listed, then repeat. Open Workout again and it suggests the next day.</small>
      </div>
    );
  }
  if (picking) {
    const have = new Set(library.map((l) => key(l.name)));
    return (
      <div className="wk">
        <header><b>Add exercise</b><button onClick={() => setPicking(null)}>Back</button></header>
        <input className="wk-in" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
        {MUSCLES.map((mu) => {
          // The catalog is long: show the first 12 per muscle until you search (curated common ones come first).
          const list = catalog.filter((c) => c.muscle === mu && !have.has(key(c.name)) && `${c.name} ${c.muscle} ${c.machine} ${c.kind}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, q.trim() ? 60 : 12);
          return list.length ? (
            <div key={mu}>
              <small className="wk-hint">{mu}</small>
              {list.map((c) => <button className="wk-add" key={c.name} onClick={() => pick(c)}>{c.name} · {c.kind} · {c.style}</button>)}
            </div>
          ) : null;
        })}
        <button className="wk-add" onClick={() => pick(null)}>Other (type a name)…</button>
      </div>
    );
  }
  if (view === 'library') {
    return (
      <div className="wk">
        <header><b>Library · {library.length} exercises</b><button onClick={() => setView('today')}>Back</button></header>
        <small className="wk-hint">Push/pull drives the order: they alternate, and each session starts with the opposite of the last. Renaming also renames the history.</small>
        {[...library].sort((a, b) => a.name.localeCompare(b.name)).map((l) => (
          <div className="wk-lib" key={`${l.id}-${l.name}`}>
            <button className="wk-libhead" onClick={() => setLibOpen(libOpen === l.id ? null : l.id)}>
              <i className="wk-arrow" style={{ display: 'inline-block', transform: libOpen === l.id ? 'rotate(90deg)' : 'none' }}>›</i>
              <b>{l.name}</b>
              <small>{[l.muscle, l.kind, l.style, l.movement].filter(Boolean).join(' · ') || 'not classified'}</small>
            </button>
            {libOpen === l.id && <>
            <input className="wk-in wk-lib-name" defaultValue={l.name} onBlur={(e) => saveLib(l, { name: e.target.value })} />
            <Chips options={KINDS} value={l.kind} onPick={(v) => saveLib(l, { kind: v })} />
            <Chips options={MUSCLES} value={l.muscle} onPick={(v) => saveLib(l, { muscle: v })} />
            <Chips options={STYLES} value={l.style} onPick={(v) => saveLib(l, { style: v })} />
            <Chips options={MACHINES} value={l.machine} onPick={(v) => saveLib(l, { machine: v })} />
            <Chips options={MOVEMENTS} value={l.movement} onPick={(v) => saveLib(l, { movement: v })} />
            <input className="wk-in" placeholder="notes" defaultValue={l.notes || ''} onBlur={(e) => saveLib(l, { notes: e.target.value })} />
            </>}
          </div>
        ))}
        <button className="wk-add" onClick={() => setPicking('library')}>+ Add exercise</button>
      </div>
    );
  }

  if (view === 'stats') {
    const rows = buildStats(sessions);
    // Group by muscle first (set in the Library), then exercise.
    const groups = [...MUSCLES, ''].map((mu) => [mu || 'unclassified', rows.filter((r) => ((libMap[r.k] || {}).muscle || '') === mu)]).filter(([, l]) => l.length);
    const gAvg = (list) => { const p = list.filter((r) => r.start); return p.length ? p.reduce((a, r) => a + r.pct, 0) / p.length : null; };
    const dates = sessions.map((x) => new Date(x.performed_at)).sort((a, b) => a - b);
    const num = (n) => Number(n.toFixed(1)).toString();
    return (
      <div className="wk">
        <header><b>Stats · {sessions.length} sessions{dates.length ? ` since ${day(dates[0])}` : ''}</b><button onClick={() => setView('today')}>Back</button></header>
        <table className="wk-stats">
          <thead><tr><th>Exercise</th><th>Start</th><th>Now</th><th>Change</th><th>Trend</th><th>#</th></tr></thead>
          <tbody>
            {groups.map(([mu, list]) => (
              <React.Fragment key={mu}>
                <tr className="wk-group" onClick={() => setOpenGroups((g) => ({ ...g, [mu]: !g[mu] }))}><td colSpan="6"><i className="wk-arrow" style={{ display: 'inline-block', transform: openGroups[mu] ? 'rotate(90deg)' : 'none', marginRight: 6 }}>›</i><b>{mu}</b> · {list.length} exercise{list.length > 1 ? 's' : ''}{gAvg(list) != null ? ` · avg ${gAvg(list) > 0 ? '+' : ''}${gAvg(list).toFixed(0)}%` : ''}</td></tr>
                {openGroups[mu] && list.map((r) => (
              <React.Fragment key={r.k}>
                <tr onClick={() => setStatOpen(statOpen === r.k ? null : r.k)}>
                  <td>{r.name}</td><td>{r.start != null ? num(r.start) : '–'}</td><td><b>{r.now != null ? num(r.now) : '–'}</b></td>
                  <td className={r.delta > 0 ? 'wk-pos' : ''}>{r.start != null ? `${r.delta > 0 ? '+' : ''}${num(r.delta)} (${r.pct > 0 ? '+' : ''}${r.pct.toFixed(0)}%)` : ''}</td>
                  <td><Spark v={r.w} /></td><td>{r.n}</td>
                </tr>
                {statOpen === r.k && (
                  <tr><td colSpan="6" className="wk-hist">
                    {[...r.h].reverse().map((e, i) => <div key={i}>{day(e.date)} <Fmt e={e} plain />{e.notes ? <small> · {e.notes}</small> : null}</div>)}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const count = Object.keys(done).length;
  return (
    <div className="wk">
      <header>
        <b>Workout · {day(new Date())}</b>
        <span className="wk-nav">
          <button className="wk-burger" onClick={() => setMenu(!menu)} aria-label="Menu" aria-expanded={menu}>☰</button>
          {menu && (
            <div className="wk-menu">
              {[['Library', () => setView('library')], ['Stats', () => setView('stats')], ['Log', () => setView('log')], ...(isAdmin ? [['Friends', () => { setView('friends'); api.get('/workouts/friends').then((r) => setFriends(r.data)).catch(() => {}); }]] : []), [isDarkMode ? 'Light mode' : 'Dark mode', onToggleTheme], ['Sign out', onSignOut]].map(([label, fn]) => (
                <button key={label} onClick={() => { setMenu(false); fn(); }}>{label}</button>
              ))}
            </div>
          )}
        </span>
      </header>

      <div className="wk-prog">
        <Chips options={['All', ...Object.keys(programs)]} value={program} onPick={(v) => { if (v) { setProgram(v); setDayPick(''); } }} />
        <button onClick={() => setEditing({ orig: null, name: '', days: [{ name: '', muscles: [] }] })} aria-label="New split">+</button>
        {program !== 'All' && <button onClick={() => setEditing({ orig: program, name: program, days: programs[program].map((d) => ({ ...d })) })} aria-label="Edit split">✎</button>}
      </div>
      {days.length > 1 && <>
        {!welcomed && count === 0 && (
          <div className="wk-welcome">
            {lastDay ? <>Last session looked like <b>{lastDay}</b> ({day(sessions[0].performed_at)}). </> : null}
            Next in {program}: <b>{suggested}</b>.
            <button className="wk-primary" onClick={() => { setDayPick(suggested); setWelcomed(true); }}>Start {suggested}</button>
          </div>
        )}
        <Chips options={dayNames} value={activeDay} onPick={(v) => { setDayPick(v); setWelcomed(true); }} />
        <small className="wk-hint">{dayPick ? 'Day picked by you' : `Next in rotation: ${suggested}`} · only {activeDay} exercises show here, the rest sit under Other exercises.</small>
      </>}
      <button className="wk-add" onClick={() => {
        // anchors: what is already logged today, then whatever sits first among the rest
        const logged = typical.filter((m) => done[key(m.name)]);
        const firstOpen = typical.find((m) => !done[key(m.name)]);
        setOrder(restOrder(typical, firstOpen ? [...logged, firstOpen] : logged, libMap).map((m) => key(m.name)));
      }}>Auto order the rest</button>
      {[...typical, ...(showOther ? other : [])].map(({ name, last }) => {
        const k = key(name);
        const d = done[k];
        const fin = !!(d && d.completed);
        const rec = last && recommend(last, sessions.flatMap((sess) => sess.exercises.filter((e) => key(e.name) === k)).slice(0, 4));
        const isOther = other.some((m) => key(m.name) === k);
        const lib = libMap[k];
        const was = Object.keys(swaps).find((o) => key(swaps[o]) === k);
        const tags = lib || was ? <>{was && <i className="wk-kind">swap for {was}</i>}{lib && lib.kind && lib.kind !== 'other' && <i className="wk-kind">{lib.kind}</i>}{lib && lib.machine && lib.machine !== 'Machine' && <i className="wk-kind">{lib.machine}</i>}</> : null;
        if (open && key(open.name) === k) {
          return (
            <div className={`wk-card wk-open ${fin ? 'wk-donebar' : ''}`} key={k} data-key={k}>
              <button className="wk-chev wk-chev-open" onClick={() => setOpen(null)} aria-label="Close"><i className="wk-arrow">›</i><b>{name}</b>{tags}{fin && <i className="wk-check" />}</button>
              {(() => {
                // the last three times you did this exact exercise, newest first, to see the trend
                const hist = sessions.flatMap((sess) => sess.exercises.filter((e) => key(e.name) === k).map((e) => ({ ...e, date: sess.performed_at }))).slice(0, 3);
                return hist.length ? (
                  <div className="wk-hist3">
                    {hist.map((h, i) => <small key={i}>{i === 0 ? 'Last' : ''} {day(h.date)}: <Fmt e={h} plain />{h.notes ? ` · ${h.notes}` : ''}</small>)}
                  </div>
                ) : null;
              })()}
              {!done[key(open.name)] && <button onClick={() => setSwapping(open.name)}>Swap for another variant</button>}
              {lib && (!lib.kind || !lib.muscle || !lib.style || !lib.machine || lib.machine === 'Machine') && (
                <>
                  <small>Classify once, so the order can rest your muscles:</small>
                  <Chips options={KINDS} value={lib.kind} onPick={(v) => saveLib(lib, { kind: v })} />
                  <Chips options={MUSCLES} value={lib.muscle} onPick={(v) => saveLib(lib, { muscle: v })} />
                  <Chips options={STYLES} value={lib.style} onPick={(v) => saveLib(lib, { style: v })} />
                  <Chips options={MACHINES} value={lib.machine} onPick={(v) => saveLib(lib, { machine: v })} />
                </>
              )}
              {[['weight', 'Weight', step(Number(open.weight) || 0)], ['sets', 'Sets', 1]].map(([f, label, st]) => (
                <div className="wk-row" key={f}>
                  <span>{label}</span>
                  <button onClick={() => bump(f, -st)}>−</button>{f === 'weight'
                    ? <input className="wk-num" type="text" inputMode="decimal" value={open.weight ?? ''} onFocus={(e) => e.target.select()} onChange={(e) => update({ weight: e.target.value })} />
                    : <b>{open[f]}</b>}<button onClick={() => bump(f, st)}>+</button>
                </div>
              ))}
              {eff(open.effort, open.sets).map((cur, i) => (
                <div className="wk-row" key={i}>
                  <span>Set {i + 1}</span>
                  <button onClick={() => setReps(i, -1)}>−</button><b>{repsOf(open)[i]}</b><button onClick={() => setReps(i, 1)}>+</button>
                  {['l', 'm', 'h'].map((x) => (
                    <button key={x} className={cur === x ? `on wk-bg-${x}` : `wk-t-${x}`} onClick={() => {
                      const a = eff(open.effort, open.sets); a[i] = cur === x ? '-' : x; update({ effort: a.join('') });
                    }}>{x.toUpperCase()}</button>
                  ))}
                </div>
              ))}
              <textarea className="wk-note" rows={2} placeholder="Note (form, cheat reps …)" value={open.notes || ''}
                onChange={(e) => update({ notes: e.target.value })} />
              {fin && <button onClick={() => undo(k)}>Undo – not done</button>}
              {d && <button onClick={() => remove(k)}>Remove from session</button>}
            </div>
          );
        }
        return (
          <div className={`wk-card ${fin ? 'wk-done' : ''}`} key={k} data-key={k} style={cardStyle(k, d, isOther)}
            onTouchStart={(e) => {
              if (e.target.closest('.wk-handle,button,textarea')) return;
              sw.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, mode: null };
            }}
            onTouchMove={(e) => {
              const st = sw.current;
              if (!st) return;
              const dx = e.touches[0].clientX - st.x;
              const dy = e.touches[0].clientY - st.y;
              if (!st.mode) {
                if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
                st.mode = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
              }
              if (st.mode !== 'x') return;
              // left hides (not for done ones), right restores
              setSwipe({ k, dx: isOther ? Math.max(0, dx) : d ? 0 : Math.min(0, dx) });
            }}
            onTouchEnd={() => {
              const st = sw.current; sw.current = null;
              const dx = swipe && swipe.k === k ? swipe.dx : 0;
              setSwipe(null);
              if (!st || st.mode !== 'x' || Math.abs(dx) < 80) return;
              setPref((p) => ({ ...p, [k]: dx < 0 ? 'hide' : 'pin' }));
            }}>
            {!isOther && <span className="wk-handle" onMouseDown={(e) => e.preventDefault()} onPointerDown={(e) => startDrag(e, k)}>≡</span>}
            <div onClick={() => setOpen(toEntry(name, d || rec || last || {}))}>
              <span className="wk-chev" aria-label="Open"><i className="wk-arrow">›</i><b>{name}</b>{tags}{fin && <i className="wk-check" />}</span>
              <small>{d
                ? <>Today: <Fmt e={d} />{fin && gain(d, last) && <i className="wk-up">{gain(d, last)}</i>}{d.notes ? ` · ${d.notes}` : ''}{!d.effort || d.effort.includes('-') ? ' · level missing' : ''}</>
                : last ? <>Last {day(last.date)}: <Fmt e={last} plain /></> : 'New exercise'}</small>
              {!d && rec && <small className="wk-rec">→ <Fmt e={rec} /> · {rec.why}</small>}
            </div>
            {fin && <button onClick={() => undo(k)}>Undo</button>}
            {!d && last && (
              <div className="wk-row">
                <button onClick={() => log(toEntry(name, { ...last, effort: null, notes: null }))}>Same</button>
                <button className="wk-primary" onClick={() => log(toEntry(name, rec))}>Suggested</button>
              </div>
            )}
          </div>
        );
      })}

      {other.length > 0 && <button className="wk-add" onClick={() => setShowOther(!showOther)}>{showOther ? 'Hide' : 'Other exercises'} ({other.length})</button>}
      <button className="wk-add" onClick={() => setPicking('today')}>+ New exercise</button>
      <textarea className="wk-note" rows={2} placeholder="Note for the whole session (food, sleep, form …)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button className="wk-save" onClick={save} disabled={saving || !count}>Save session ({count})</button>
      {(count > 0 || note) && <button className="wk-scrap" onClick={() => { if (window.confirm('Scrap this draft session?')) { setDone({}); setSwaps({}); setNote(''); setOrder([]); setOpen(null); } }}>Scrap draft</button>}
    </div>
  );
}
